/**
 * storageCleanup
 * ──────────────
 * Helpers to reclaim space in the public `sponsor-creatives` bucket when a
 * creative (image or video) is replaced or removed. Uploads always write a
 * fresh randomly-named object, so without cleanup every replacement leaves
 * the previous file orphaned in storage forever.
 *
 * Safety: deletion is always BEST-EFFORT and must never block or fail a
 * user's save. We only ever delete objects that live in OUR bucket (a
 * pasted external URL is left untouched), and callers are responsible for
 * only passing a URL that is no longer referenced by any saved record.
 */

import { supabase } from '@/lib/supabase';

const BUCKET = 'sponsor-creatives';

/**
 * Extract the object path within `sponsor-creatives` from a public URL,
 * or null if the URL doesn't point into our bucket (e.g. an external URL
 * the operator pasted, which we must not touch).
 */
export function pathInSponsorBucket(url: string | null | undefined): string | null {
  if (!url) return null;
  const marker = `/storage/v1/object/public/${BUCKET}/`;
  const i = url.indexOf(marker);
  if (i === -1) return null;
  const path = url.slice(i + marker.length).split('?')[0];
  return path ? decodeURIComponent(path) : null;
}

/**
 * Best-effort delete of an object we previously uploaded to the
 * sponsor-creatives bucket. No-ops for external URLs, empty input, or on
 * any error — cleanup should never interrupt the primary flow.
 */
export async function deleteSponsorCreativeByUrl(
  url: string | null | undefined,
): Promise<void> {
  const path = pathInSponsorBucket(url);
  if (!path) return;
  try {
    await supabase.storage.from(BUCKET).remove([path]);
  } catch {
    /* best-effort: an orphaned file is not worth surfacing an error */
  }
}
